/**
 * Intent Detector - Rileva l'intento dell'utente dalla domanda
 *
 * Analizza il messaggio e determina:
 * - Quale dominio riguarda (progetti, task, calendario, contatti, contabilità)
 * - Che tipo di operazione (read, create, update, delete)
 * - Parametri rilevanti (filtri, nomi, date)
 */

export type Domain = 'projects' | 'tasks' | 'calendar' | 'contacts' | 'accounting' | 'general';
export type Operation = 'read' | 'create' | 'update' | 'delete' | 'chat';

export interface DetectedIntent {
  domain: Domain;
  operation: Operation;
  confidence: number; // 0-1
  filters?: {
    status?: string;
    name?: string;
    date?: string;
    projectId?: string;
  };
  rawQuery: string;
}

// Keywords per ogni dominio
const DOMAIN_KEYWORDS: Record<Domain, string[]> = {
  projects: [
    'progett', 'project', 'lavoro', 'lavori', 'cliente', 'clienti',
    'budget', 'scadenz', 'deadline', 'milestone'
  ],
  tasks: [
    'task', 'attività', 'todo', 'da fare', 'completat', 'in corso',
    'assegnat', 'priorità', 'urgent'
  ],
  calendar: [
    'event', 'calendario', 'appuntament', 'riunion', 'meeting',
    'oggi', 'domani', 'settimana', 'mese', 'quando', 'orario'
  ],
  contacts: [
    'contatt', 'persona', 'email', 'telefon', 'chiamat', 'messaggio',
    'lead', 'deal', 'opportunità', 'vendita'
  ],
  accounting: [
    'fattur', 'pagament', 'incass', 'spesa', 'entrat', 'uscit',
    'iva', 'conto', 'bilancio', 'denaro', 'soldi', 'euro'
  ],
  general: []
};

// Keywords per operazioni
const OPERATION_KEYWORDS: Record<Operation, string[]> = {
  read: [
    'mostr', 'elenc', 'list', 'quali', 'quanti', 'dimmi', 'cerca',
    'trova', 'vedi', 'visualizza', 'ho', 'ci sono', 'esistono'
  ],
  create: [
    'crea', 'aggiungi', 'nuovo', 'nuova', 'inserisci', 'fai', 'metti'
  ],
  update: [
    'modifica', 'aggiorna', 'cambia', 'sposta', 'rinomina', 'segna',
    'completa', 'chiudi', 'riapri'
  ],
  delete: [
    'elimina', 'cancella', 'rimuovi', 'togli', 'delete'
  ],
  chat: []
};

// Keywords per status
const STATUS_KEYWORDS: Record<string, string[]> = {
  active: ['attiv', 'in corso', 'aperti', 'aperte'],
  completed: ['completat', 'finit', 'chiusi', 'chiuse', 'fatti', 'fatte'],
  planning: ['pianificazione', 'da iniziare', 'non iniziati'],
  on_hold: ['in pausa', 'sospesi', 'sospese', 'bloccati'],
  cancelled: ['annullat', 'cancellat']
};

/**
 * Normalizza il testo per la ricerca
 */
function normalize(text: string): string {
  return text.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // Rimuove accenti
}

/**
 * Conta quanti keywords matchano nel testo
 */
function countMatches(text: string, keywords: string[]): number {
  const normalized = normalize(text);
  return keywords.filter(kw => normalized.includes(normalize(kw))).length;
}

/**
 * Rileva il dominio principale della domanda
 */
function detectDomain(text: string): { domain: Domain; confidence: number } {
  const normalized = normalize(text);
  let bestDomain: Domain = 'general';
  let bestScore = 0;

  for (const [domain, keywords] of Object.entries(DOMAIN_KEYWORDS)) {
    if (domain === 'general') continue;

    const score = countMatches(normalized, keywords);
    if (score > bestScore) {
      bestScore = score;
      bestDomain = domain as Domain;
    }
  }

  // Confidence basata sul numero di match
  const confidence = bestScore > 0 ? Math.min(0.9, 0.3 + (bestScore * 0.2)) : 0;

  return { domain: bestDomain, confidence };
}

/**
 * Rileva l'operazione richiesta
 */
function detectOperation(text: string): Operation {
  const normalized = normalize(text);

  // Controlla in ordine di priorità (delete prima perché più specifico)
  for (const op of ['delete', 'create', 'update', 'read'] as Operation[]) {
    if (countMatches(normalized, OPERATION_KEYWORDS[op]) > 0) {
      return op;
    }
  }

  // Default: se parla di un dominio, probabilmente vuole leggere
  return 'read';
}

/**
 * Rileva filtri di status
 */
function detectStatusFilter(text: string): string | undefined {
  const normalized = normalize(text);

  for (const [status, keywords] of Object.entries(STATUS_KEYWORDS)) {
    if (countMatches(normalized, keywords) > 0) {
      return status;
    }
  }

  return undefined;
}

/**
 * Rileva l'intento completo dalla domanda
 */
export function detectIntent(query: string): DetectedIntent {
  const { domain, confidence } = detectDomain(query);
  const operation = detectOperation(query);

  const intent: DetectedIntent = {
    domain,
    operation: confidence > 0 ? operation : 'chat',
    confidence,
    rawQuery: query,
  };

  // Aggiungi filtri se rilevati
  const statusFilter = detectStatusFilter(query);
  if (statusFilter) {
    intent.filters = { status: statusFilter };
  }

  return intent;
}

/**
 * Verifica se l'intento richiede dati dal DB
 */
export function requiresDataFetch(intent: DetectedIntent): boolean {
  return intent.confidence > 0.2 && intent.operation !== 'chat';
}
